import { useCallback, useEffect, useRef, useState } from "react"
import { useNavigate } from "@tanstack/react-router"
import { format } from "date-fns"
import {
	IconChevronRightOutline18 as ChevronRight,
	IconPenWriting3Outline18 as RenameIcon,
	IconTreePalmOutline18 as TreePalm,
	IconArrowUpFromLineOutline18 as UploadIcon,
} from "nucleo-ui-outline-18"

import type { Membership, User } from "@productlane/zero"
import { MembershipRole } from "@productlane/db/browser"
import { cn } from "@productlane/lib/src/cn"
import { isOutOfOffice } from "@productlane/lib/src/out-of-office"
import {
	AlertDialog,
	Avatar,
	Badge,
	IconButton,
	Loader,
	Pill,
	Tooltip,
} from "@productlane/ui"
import {
	DropdownMenu,
	DropdownMenuGroup,
	DropdownMenuItem,
} from "@productlane/ui/src/components/internal/dropdown"
import { EllipsisVerticalIcon as MoreVerticalIcon } from "@productlane/ui/src/icons/project/ellipsis-icon"

import { AvatarCropDialog } from "@/components/avatar-crop-dialog"
import { toast } from "@/components/toast"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { useSession } from "@/lib/session"
import { ChangeRoleDialog } from "./change-role-dialog"
import { roleChangeErrorMessage } from "./member-roles"

const FormattedRoleText: Record<MembershipRole, string> = {
	ADMIN: "Admin",
	USER: "Member",
	VIEWER: "Viewer",
}

export function MemberItem({
	membership,
	highlighted = false,
}: {
	membership: Membership & { user: User }
	highlighted?: boolean
}) {
	const { session } = useSession()
	const navigate = useNavigate()
	const openDetails = useCallback(
		() =>
			void navigate({ to: "/members/$id", params: { id: membership.user.id } }),
		[navigate, membership.user.id],
	)
	const rowRef = useRef<HTMLDivElement>(null)
	const [showHighlight, setShowHighlight] = useState(highlighted)

	useEffect(() => {
		if (!highlighted) return
		setShowHighlight(true)
		rowRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })
		const timer = setTimeout(() => setShowHighlight(false), 5000)
		return () => clearTimeout(timer)
	}, [highlighted])
	const [isEditingName, setIsEditingName] = useState(false)
	const [changeRoleDialogOpen, setChangeRoleDialogOpen] = useState(false)
	const [confirmKickMemberDialogOpen, setConfirmKickMemberDialogOpen] =
		useState(false)

	const context = api.useUtils()

	const isAdmin = session?.membershipRole === MembershipRole.ADMIN
	const imageInput = useRef<HTMLInputElement | null>(null)
	const [imagePreview, setImagePreview] = useState<string | null>(null)
	const [isUploadingImage, setIsUploadingImage] = useState(false)
	const [cropSource, setCropSource] = useState<{
		url: string
		fileName: string
		fileType: string
	} | null>(null)

	const getSignedUrlMutation = api.file.getUrl.useMutation()
	const updateMemberImageMutation =
		api.workspace.updateMemberImage.useMutation()
	const updateMemberNameMutation = api.workspace.updateMemberName.useMutation()

	const nameInputRef = useRef<HTMLInputElement | null>(null)
	const persistedName = membership.user.name ?? ""
	const [nameDraft, setNameDraft] = useState(persistedName)
	// Escape blurs the field in the tick it reverts, so the blur handler reads the ref, not the state.
	const nameDraftRef = useRef(persistedName)

	const setName = useCallback((value: string) => {
		nameDraftRef.current = value
		setNameDraft(value)
	}, [])

	useEffect(() => {
		setName(persistedName)
	}, [persistedName, setName])

	const saveName = useCallback(async () => {
		const trimmed = nameDraftRef.current.trim()
		if (trimmed === persistedName) return
		if (!trimmed) {
			setName(persistedName)
			toast.info({
				title: "Enter a name for this member",
				icon: RenameIcon,
			})
			return
		}
		try {
			await updateMemberNameMutation.mutateAsync({
				membershipId: membership.id,
				name: trimmed,
			})
			await context.workspace.members.invalidate()
			analytics.track("update-member-name", {
				targetUserId: membership.user.id,
			})
		} catch {
			setName(persistedName)
			toast.error({
				title: "The name didn't save",
				description: "Try again.",
				icon: RenameIcon,
			})
		}
	}, [
		context.workspace.members,
		membership.id,
		membership.user.id,
		persistedName,
		setName,
		updateMemberNameMutation,
	])

	const uploadImage = useCallback(
		async (file: File, previewUrl: string) => {
			setImagePreview(previewUrl)
			setIsUploadingImage(true)
			try {
				const { fileUrl, uploadUrl } = await getSignedUrlMutation.mutateAsync({
					fileName: file.name,
					fileType: file.type,
				})
				const res = await fetch(uploadUrl, {
					method: "PUT",
					body: file,
					headers: { "Content-Type": file.type },
				})
				if (!res.ok) throw new Error("File upload failed")

				await updateMemberImageMutation.mutateAsync({
					membershipId: membership.id,
					imageUrl: fileUrl,
				})
				await context.workspace.members.invalidate()
				analytics.track("update-member-image", {
					targetUserId: membership.user.id,
				})
				toast.success({
					title: `Updated ${membership.user.name ?? "the member"}'s profile picture`,
					icon: UploadIcon,
				})
			} catch {
				setImagePreview(null)
				toast.error({
					title: "The profile picture didn't upload",
					description: "Try again with the same image.",
					icon: UploadIcon,
				})
			} finally {
				setIsUploadingImage(false)
			}
		},
		[
			context.workspace.members,
			getSignedUrlMutation,
			membership.id,
			membership.user.id,
			membership.user.name,
			updateMemberImageMutation,
		],
	)

	const kickMemberMutation = api.workspace.kickMember.useMutation({
		onSuccess: async () => {
			await context.workspace.members.invalidate()
			void context.workspace.trialInfo.invalidate()
			toast.success({
				description: "Successfully removed member!",
				title: "Member Removed",
			})
		},
	})

	const makeAdminMutation = api.workspace.makeAdmin.useMutation({
		onSuccess: async () => {
			await context.workspace.members.invalidate()
			void context.workspace.trialInfo.invalidate()
			toast.success({
				description: "User is now an admin.",
				title: "Success",
			})
		},
	})
	const makeMemberMutation = api.workspace.makeMember.useMutation({
		onSuccess: async () => {
			await context.workspace.members.invalidate()
			void context.workspace.trialInfo.invalidate()
			toast.success({
				description: "User is now a member.",
				title: "Success",
			})
		},
	})
	const makeViewerMutation = api.workspace.makeViewer.useMutation({
		onSuccess: async () => {
			await context.workspace.members.invalidate()
			void context.workspace.trialInfo.invalidate()
			toast.success({
				description: "User is now a viewer.",
				title: "Success",
			})
		},
	})

	const changeRole = async (role: MembershipRole) => {
		analytics.track("change-member-role", {
			targetUserId: membership.user.id,
			role,
		})
		try {
			if (role === MembershipRole.ADMIN) {
				await makeAdminMutation.mutateAsync({ membershipId: membership.id })
			} else if (role === MembershipRole.USER) {
				await makeMemberMutation.mutateAsync({ membershipId: membership.id })
			} else {
				await makeViewerMutation.mutateAsync({ membershipId: membership.id })
			}
		} catch (error) {
			toast.error({
				title: "The role didn't change",
				description: roleChangeErrorMessage(error),
			})
		}
	}

	return (
		<>
			<div
				ref={rowRef}
				role="button"
				tabIndex={0}
				// React bubbles portal events (the menu, its dialogs) through this row, so act only on clicks inside its own DOM.
				onClick={(e) => {
					if (e.currentTarget.contains(e.target as Node)) openDetails()
				}}
				onKeyDown={(e) => {
					if (e.target !== e.currentTarget) return
					if (e.key === "Enter" || e.key === " ") {
						e.preventDefault()
						openDetails()
					}
				}}
				className={cn(
					"group -mx-2 flex cursor-default items-center gap-4 rounded-lg border border-transparent p-2 hover:bg-gray-100 dark:hover:bg-gray-900",
					showHighlight && "border-green-500",
				)}
			>
				{isAdmin ? (
					<button
						type="button"
						title="Upload a profile picture"
						className="group/avatar relative flex flex-none rounded-md"
						disabled={isUploadingImage}
						onClick={(e) => {
							e.stopPropagation()
							imageInput.current?.click()
						}}
					>
						<Avatar
							name={membership.user.name ?? ""}
							image={imagePreview ?? membership.user.imageUrl}
							roundness="full"
						/>
						<span
							className={cn(
								"absolute inset-0 flex items-center justify-center rounded-md bg-black/50 opacity-0 transition-opacity group-hover/avatar:opacity-100",
								isUploadingImage && "opacity-100",
							)}
						>
							{isUploadingImage ? (
								<Loader className="text-white" />
							) : (
								<UploadIcon className="h-4 w-4 text-white" />
							)}
						</span>
					</button>
				) : (
					<Avatar
						name={membership.user.name ?? ""}
						image={membership.user.imageUrl}
						roundness="full"
					/>
				)}
				<div className="min-w-0 flex-1 text-sm">
					{isAdmin ? (
						<div
							data-testid="member-name-row"
							className="group/name flex items-center gap-1.5"
						>
							{/* The mirror span sizes the grid cell, so the field ends where the name ends and the pen sits next to it. */}
							<span className="-ml-1 inline-grid min-w-0">
								<span
									aria-hidden
									className="invisible col-start-1 row-start-1 truncate whitespace-pre px-1 font-medium"
								>
									{nameDraft || " "}
								</span>
								<input
									ref={nameInputRef}
									name="member-name"
									aria-label="Member name"
									value={nameDraft}
									title="Edit this member's name"
									autoComplete="off"
									maxLength={100}
									// Without it the field keeps its 20-character intrinsic width and the grid cell stops tracking the name.
									size={1}
									className="col-start-1 row-start-1 w-full min-w-0 cursor-text truncate border-none bg-transparent px-1 font-medium outline-none"
									onClick={(e) => e.stopPropagation()}
									onChange={(e) => setName(e.target.value)}
									onFocus={() => setIsEditingName(true)}
									onBlur={() => {
										setIsEditingName(false)
										void saveName()
									}}
									onKeyDown={(e) => {
										if (e.key === "Enter") {
											e.currentTarget.blur()
										}
										if (e.key === "Escape") {
											setName(persistedName)
											e.currentTarget.blur()
										}
									}}
								/>
							</span>
							<button
								type="button"
								tabIndex={-1}
								aria-hidden
								onClick={(e) => {
									e.stopPropagation()
									nameInputRef.current?.focus()
								}}
								className={cn(
									"flex flex-none cursor-default items-center text-tertiary opacity-0",
									!isEditingName && "group-hover:opacity-100",
								)}
							>
								<RenameIcon className="h-3.5 w-3.5 flex-none" />
							</button>
						</div>
					) : (
						<div className="truncate font-medium capitalize">
							{membership.user.name}
						</div>
					)}
					<div className="truncate text-secondary">{membership.user.email}</div>
				</div>
				{isOutOfOffice(membership.outOfOfficeUntil) && (
					<Tooltip
						label={`Away until ${format(membership.outOfOfficeUntil!, "d MMM")}`}
					>
						<Badge variant="amber" size="sm" Icon={TreePalm}>
							Away
						</Badge>
					</Tooltip>
				)}
				<Pill
					variant={membership.role === "ADMIN" ? "green" : "dark-gray"}
					className="capitalize"
					size="sm"
				>
					{FormattedRoleText[membership.role as MembershipRole]}
				</Pill>
				<IconButton
					Icon={ChevronRight}
					variant="subtle"
					size="md"
					aria-label="Open member details"
					className="flex-none opacity-0 group-hover:opacity-100"
					iconClassName="h-3.5 w-3.5"
					onClick={(e) => {
						e.stopPropagation()
						openDetails()
					}}
				/>
				{membership.user.id !== session?.userId &&
				session?.membershipRole === MembershipRole.ADMIN ? (
					<DropdownMenu
						trigger={
							<IconButton
								Icon={MoreVerticalIcon}
								variant="dark-ghost"
								size="md"
								iconClassName="fill-current"
								onClick={(e) => e.stopPropagation()}
							/>
						}
						contentProps={{ side: "bottom", align: "end" }}
					>
						<DropdownMenuGroup>
							<DropdownMenuItem
								onClick={() =>
									void navigate({
										to: "/members/$id",
										params: { id: membership.user.id },
									})
								}
							>
								View details
							</DropdownMenuItem>
							<DropdownMenuItem onClick={() => setChangeRoleDialogOpen(true)}>
								Change role
							</DropdownMenuItem>

							<DropdownMenuItem
								onClick={() => {
									setConfirmKickMemberDialogOpen(true)
								}}
							>
								Remove member
							</DropdownMenuItem>
						</DropdownMenuGroup>
					</DropdownMenu>
				) : (
					<div className="w-6" />
				)}
			</div>

			{isAdmin ? (
				<>
					<input
						name="member-image"
						type="file"
						accept="image/*"
						ref={imageInput}
						className="hidden"
						onChange={(e) => {
							const file = e.target.files?.[0]
							if (file) {
								setCropSource({
									url: URL.createObjectURL(file),
									fileName: file.name,
									fileType: file.type,
								})
							}
							e.target.value = ""
						}}
					/>
					{cropSource ? (
						<AvatarCropDialog
							isOpen
							onOpenChange={(open) => {
								if (!open) {
									URL.revokeObjectURL(cropSource.url)
									setCropSource(null)
								}
							}}
							imageSrc={cropSource.url}
							fileName={cropSource.fileName}
							fileType={cropSource.fileType}
							onCropComplete={(croppedFile, previewUrl) => {
								void uploadImage(croppedFile, previewUrl)
							}}
						/>
					) : null}
				</>
			) : null}

			<ChangeRoleDialog
				isOpen={changeRoleDialogOpen}
				onOpenChange={setChangeRoleDialogOpen}
				memberName={membership.user.name ?? ""}
				currentRole={membership.role as MembershipRole}
				onSave={changeRole}
				isLoading={
					makeAdminMutation.isLoading ||
					makeMemberMutation.isLoading ||
					makeViewerMutation.isLoading
				}
			/>

			<AlertDialog
				isOpen={confirmKickMemberDialogOpen}
				onOpenChange={setConfirmKickMemberDialogOpen}
				confirmText={`Confirm`}
				title={`Do you want to remove ${membership.user.name} from this workspace?`}
				description="This person will no longer have access to this workspace."
				onCancel={() => {
					analytics.track("kick-member-cancel")
					setConfirmKickMemberDialogOpen(false)
				}}
				onConfirm={async () => {
					analytics.track("kick-member", {
						targetUserId: membership.user.id,
					})

					try {
						await kickMemberMutation.mutateAsync({
							membershipId: membership.id,
						})
					} catch (error) {
						const isGuard =
							(error as { data?: { code?: string } }).data?.code === "FORBIDDEN"
						;(isGuard ? toast.info : toast.error)({
							title: `${membership.user.name ?? "This member"} wasn't removed`,
							description:
								(error as { message?: string }).message ?? "Try again.",
						})
					}
				}}
			/>
		</>
	)
}
